import { TRACKING_PHONE, TRACKING_PHONE_HREF, type SeverityLevel } from "../../constants";
import { trackPhoneClick } from "../../lib/analytics";
import { PhoneIcon } from "../icons/PestIcons";

interface DiagnosticReportProps {
  city: string;
  sqFt: number;
  costMin: number;
  costMax: number;
  severityLabel: string;
  severityPercent: number;
  severityLevel: SeverityLevel;
  identifiedPests: string[];
  showCallButton: boolean;
  specialtyNotice?: string;
}

export function DiagnosticReport({
  city,
  sqFt,
  costMin,
  costMax,
  severityLabel,
  severityPercent,
  severityLevel,
  identifiedPests,
  showCallButton,
  specialtyNotice,
}: DiagnosticReportProps) {
  const severityColor =
    severityLevel === "high"
      ? "bg-red-500"
      : severityLevel === "moderate"
        ? "bg-amber-500"
        : "bg-emerald-500";

  return (
    <div className="animate-fade-in p-4 sm:p-5">
      <div className="mb-4 flex items-start justify-between border-b border-slate-200 pb-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Report Generated</p>
          <h2 className="text-base font-semibold text-slate-900">Diagnostic &amp; Cost Estimation Report</h2>
        </div>
        <span className="rounded bg-slate-100 px-2 py-1 font-mono text-[10px] font-medium text-slate-500">
          {city}
        </span>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Structure Profile</p>
          <p className="mt-1 font-mono text-lg font-semibold text-slate-900">{sqFt.toLocaleString()} sq ft</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Identified Indicators</p>
          <p className="mt-1 text-sm font-medium text-slate-800">
            {identifiedPests.length > 0 ? identifiedPests.join(", ") : "Environmental signs detected"}
          </p>
        </div>
      </div>

      <div className="mt-3 rounded-lg border border-slate-200 p-3">
        <div className="flex items-end justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
              Estimated Local Solution Cost
            </p>
            <p className="mt-1 font-mono text-2xl font-bold text-slate-900">
              ${costMin.toLocaleString()} – ${costMax.toLocaleString()}
            </p>
          </div>
          <span className="text-[10px] font-medium text-slate-400">USD · Local rate adjusted</span>
        </div>
      </div>

      <div className="mt-3 rounded-lg border border-slate-200 p-3">
        <div className="mb-2 flex items-center justify-between">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
            Infestation Severity Index
          </p>
          <span className="rounded-full bg-slate-900 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
            {severityLabel}
          </span>
        </div>
        <div className="h-2.5 overflow-hidden rounded-full bg-slate-100">
          <div
            className={`h-full rounded-full transition-all duration-700 ${severityColor}`}
            style={{ width: `${severityPercent}%` }}
          />
        </div>
        <div className="mt-1.5 flex justify-between text-[9px] font-medium uppercase tracking-wider text-slate-400">
          <span>Low</span>
          <span>Moderate</span>
          <span>High</span>
        </div>
      </div>

      {specialtyNotice && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs font-semibold text-amber-900">Specialty License Required</p>
          <p className="mt-1 text-xs leading-relaxed text-amber-800">{specialtyNotice}</p>
        </div>
      )}

      {showCallButton && (
        <>
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs font-semibold text-red-900">Pest Activity Detected</p>
            <p className="mt-1 text-xs leading-relaxed text-red-800">
              Active infestations can cause structural damage costing up to $3,000 if left untreated. Lock in your
              local rate of ${costMin.toLocaleString()} – ${costMax.toLocaleString()} by speaking to a verified
              field tech.
            </p>
          </div>

          <a
            href={TRACKING_PHONE_HREF}
            onClick={() => trackPhoneClick()}
            className="call-pulse mt-3 flex w-full flex-col items-center gap-1 rounded-lg bg-emerald-700 px-4 py-3.5 text-white no-underline transition-colors hover:bg-emerald-800"
          >
            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide opacity-90">
              <PhoneIcon className="h-4 w-4" />
              Call Local Dispatch
            </span>
            <span className="text-xl font-bold tracking-wide">{TRACKING_PHONE}</span>
            <span className="text-[10px] font-medium opacity-80">Check Booking Availability</span>
          </a>

          <p className="mt-2 text-center text-[11px] text-slate-500">
            Verify local pricing with a licensed field technician in {city}.
          </p>
        </>
      )}
    </div>
  );
}
